import { FACTORY_LOG_URLS, FACTORY_RUNS_KEY, FACTORY_RUNS_TTL_MS, PAUSE_AFTER_DAYS } from '../config';

/**
 * Whether the daily experiment is paused, read from the factories' own build
 * logs (see FACTORY_LOG_URLS): the newest logs/<YYYY-MM-DD>-<slug>.md across all
 * three is the last time any factory ran. An index entry's date is no guide,
 * since an entry can be added by hand (Huttese, 20 Oct 2026), and Ben's products
 * never count: they write no factory log.
 */

const LOG_NAME = /^(\d{4}-\d{2}-\d{2})-.+\.md$/;

/** The newest run date among one factory's log file names; null when none is a build log. */
export function lastRunFromLogNames(names: string[]): string | null {
  let newest: string | null = null;
  for (const name of names) {
    const m = LOG_NAME.exec(name);
    if (m && (newest === null || m[1] > newest)) newest = m[1];
  }
  return newest;
}

/** The last run across every factory, or null when any factory couldn't be read
 *  (a factory we can't see might be running, so nothing is said). */
export function lastRunAcross(perFactory: (string | null | undefined)[]): string | null {
  if (perFactory.length === 0 || perFactory.some((d) => d === undefined)) return null;
  const dates = perFactory.filter((d): d is string => typeof d === 'string');
  return dates.length ? dates.reduce((a, b) => (b > a ? b : a)) : null;
}

/** Paused once no factory has run for PAUSE_AFTER_DAYS whole days. A run dated
 *  ahead of the viewer's clock (the factories run in another time zone) is recent. */
export function isPaused(lastRun: string, nowMs: number, days = PAUSE_AFTER_DAYS): boolean {
  const ran = Date.parse(`${lastRun}T00:00:00Z`);
  if (Number.isNaN(ran)) return false;
  return nowMs - ran > (days + 1) * 86_400_000;
}

function readCache(): string | null | undefined {
  try {
    const raw = localStorage.getItem(FACTORY_RUNS_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as { at: number; lastRun: string };
    if (typeof parsed?.at !== 'number' || typeof parsed.lastRun !== 'string') return undefined;
    if (Date.now() - parsed.at > FACTORY_RUNS_TTL_MS) return undefined;
    return parsed.lastRun;
  } catch {
    return undefined;
  }
}

function writeCache(lastRun: string): void {
  try {
    localStorage.setItem(FACTORY_RUNS_KEY, JSON.stringify({ at: Date.now(), lastRun }));
  } catch {
    // storage full/denied — cache is best-effort
  }
}

/** One factory's newest run; undefined when its logs couldn't be read. */
async function factoryLastRun(url: string): Promise<string | null | undefined> {
  try {
    const res = await fetch(url, { cache: 'no-cache', headers: { Accept: 'application/vnd.github+json' } });
    if (!res.ok) return undefined;
    const data = await res.json();
    if (!Array.isArray(data)) return undefined;
    return lastRunFromLogNames(data.map((f) => (typeof f?.name === 'string' ? f.name : '')));
  } catch {
    return undefined;
  }
}

/** The day any factory last ran (YYYY-MM-DD), or null when it can't be known.
 *  Best-effort and cached: a rate-limited or offline fetch just means no notice. */
export async function loadLastFactoryRun(): Promise<string | null> {
  const cached = readCache();
  if (cached !== undefined) return cached;
  const runs = await Promise.all(FACTORY_LOG_URLS.map(factoryLastRun));
  const lastRun = lastRunAcross(runs);
  if (lastRun) writeCache(lastRun);
  return lastRun;
}
